import { AudioPlayer } from "../ui/AudioPlayer.js";
import { Card } from "../ui/Card.js";
import { StanPusty } from "../ui/stany.js";
import type { MicHistoryEntry } from "../../lib/api.js";

interface Props {
  entries: MicHistoryEntry[];
  /**
   * Historia nie dala sie odczytac. Rozne od pustej listy i to rozroznienie ma
   * znaczenie: "nie ma jeszcze prob" zapraszalo do nagrywania w sytuacji, gdy
   * kazda kolejna proba i tak nie mialaby sie gdzie zapisac.
   */
  niedostepna?: boolean;
}

/** KP-2: format polski, czyli przecinek dziesietny. */
function liczba(wartosc: number | null, jednostka: string): string {
  return wartosc === null ? "-" : `${wartosc.toFixed(1).replace(".", ",")} ${jednostka}`;
}

/**
 * Ostatnie strzaly obok siebie.
 *
 * To nie jest ozdobnik. Pomiar bezwzgledny mowi malo - dopiero porownanie
 * dwoch podejsc do tego samego zdania pokazuje, czy zmiana sposobu mowienia
 * cokolwiek dala. Cwiczenie brzmi: powiedz to samo raz jeszcze, ale z energia.
 *
 * KARTA RENDERUJE SIE ZAWSZE. Wczesniej komponent zwracal `null` przy pustej
 * historii, czyli cala karta znikala z widoku - lamalo to L-8 (karty-siostry
 * o stalym ukladzie) i S-5 (stan mieszka w karcie, karta nie znika).
 *
 * TB-8: przy czterech kolumnach tabela nie miesci sie w 375px, a poziomy scroll
 * strony jest zakazany. Ponizej progu telefonu CSS zamienia wiersze w mini-karty
 * (etykieta kolumny wjezdza z atrybutu data-etykieta), a role ARIA sa wpisane
 * jawnie, zeby `display: block` nie zabral tabeli semantyki.
 */
export function MicHistory({ entries, niedostepna = false }: Props): React.JSX.Element {
  return (
    <Card tytul="Ostatnie próby">
      {niedostepna && entries.length === 0 ? (
        <StanPusty
          tytul="Historia jest chwilowo niedostępna"
          opis="Nie udało się odczytać wcześniejszych prób. Pomiar bieżącego nagrania działa normalnie, ale nie będzie z czym go porównać."
        />
      ) : entries.length === 0 ? (
        <StanPusty
          tytul="Nie ma jeszcze prób"
          opis="Nagraj pierwsze zdanie. Kolejne próby zbiorą się tutaj, żeby dało się porównać je ze sobą."
        />
      ) : (
        <>
          <p className="historia-lead">
            Powiedz to samo zdanie inaczej i porównaj liczby. Zmiana względem własnej poprzedniej próby znaczy
            więcej niż pojedynczy odczyt.
          </p>

          <table className="historia-tabela" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th role="columnheader" scope="col">
                  Wypowiedź
                </th>
                <th role="columnheader" scope="col">
                  Tempo
                </th>
                <th role="columnheader" scope="col">
                  Rozpiętość tonu
                </th>
                <th role="columnheader" scope="col">
                  Emocja
                </th>
                <th role="columnheader" scope="col">
                  Nagranie
                </th>
                <th role="columnheader" scope="col">
                  Audyt
                </th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {entries.map((entry) => (
                <tr role="row" key={entry.id}>
                  <td role="cell" data-etykieta="Wypowiedź" className="historia-tekst">
                    {entry.text || "brak mowy"}
                  </td>
                  <td role="cell" data-etykieta="Tempo">
                    {liczba(entry.syllablesPerSecond, "syl./s")}
                  </td>
                  <td role="cell" data-etykieta="Rozpiętość tonu">
                    {liczba(entry.f0RangeSemitones, "półtonu")}
                  </td>
                  {/* Etykieta z metodyki, nie slug. Slug jest kluczem bazy i
                      nie ma prawa pokazac sie uzytkownikowi - karta nad tabela
                      pisala "Stanowczość lub lekka irytacja", tabela pod nia
                      "stanowczosc", i wygladalo to jak dwa rozne wyniki. */}
                  <td role="cell" data-etykieta="Emocja">
                    {entry.emotionLabel ?? "-"}
                  </td>
                  {/* Odsłuch własnej próby jest sednem tego trybu: liczby mówią,
                      co się zmieniło, a nagranie mówi, jak to brzmiało. Do
                      19.08.2026 nagranie znikało razem z odpowiedzią serwera. */}
                  <td role="cell" data-etykieta="Nagranie">
                    {entry.maAudio ? (
                      <AudioPlayer zrodlo={`/api/mic/${entry.id}/audio`} etykieta="Nagranie próby" />
                    ) : (
                      <span className="historia-bez-audio">nagranie już nieprzechowywane</span>
                    )}
                  </td>
                  {/* Zwykły link, nie przycisk z fetch: przeglądarka sama zna się
                      na pobieraniu plików, a ciasteczko sesji jedzie z żądaniem.
                      Nazwę pliku narzuca serwer nagłówkiem Content-Disposition. */}
                  <td role="cell" data-etykieta="Audyt">
                    <a className="historia-audyt" href={`/api/mic/${entry.id}/audyt.zip`} download>
                      Pobierz audyt
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}
